import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Divider,
  Stack,
  Paper,
  Tabs,
  Tab,
  Card,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  Alert,
  Chip,
  useTheme,
  useMediaQuery,
  Drawer,
  AppBar,
  Toolbar
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import LockIcon from '@mui/icons-material/Lock';
import MenuIcon from '@mui/icons-material/Menu';
import { getShop, getBusinessDeliveryPreference, updateBusinessDeliveryPreferenceSimple, getEnhancedDeliveryPreference, updateBusinessDeliveryPreference } from '../../../utils/api';

const SECTIONS = [
  { label: 'Business Information', key: 'business' },
  { label: 'Invoice Preferences', key: 'invoice' },
  { label: 'Delivery Settings', key: 'delivery' },
  // Future: add more sections here
];

export default function BusinessSettings() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  
  const [tab, setTab] = useState(0);
  const [business, setBusiness] = useState({
    name: '',
    type: '',
    gstin: '',
    address: '',
    contactEmail: '',
    phone: '',
    website: '',
    logo: null,
    description: '',
    invoiceFooter: '',
    logos: [], // Added for multiple logo support
  });
  const [invoice, setInvoice] = useState({
    terms: '',
    currency: '',
  });
  const [logoIndex, setLogoIndex] = useState(0); // Added for logo slider

  // Delivery settings state
  const [businessDeliveryPlatform, setBusinessDeliveryPlatform] = useState('BOTH');
  const [effectiveDeliveryPlatform, setEffectiveDeliveryPlatform] = useState('BOTH');
  const [effectiveWhatsappSendAs, setEffectiveWhatsappSendAs] = useState('pdf');
  const [shouldSendViaWhatsapp, setShouldSendViaWhatsapp] = useState(true);
  const [shouldPrint, setShouldPrint] = useState(true);
  const [deliveryLoading, setDeliveryLoading] = useState(false);
  const [deliveryError, setDeliveryError] = useState('');
  const [deliverySuccess, setDeliverySuccess] = useState('');

  useEffect(() => {
    const shopId = localStorage.getItem('shopId');
    if (shopId) {
      getShop(shopId).then(res => {
        if (res && res.data) setBusiness(res.data);
      });
    }
  }, []);

  // Fetch delivery preferences on component mount
  useEffect(() => {
    fetchDeliveryPreferences();
  }, []);

  const fetchDeliveryPreferences = async () => {
    try {
      setDeliveryLoading(true);
      const shopId = localStorage.getItem('shopId');
      const userRole = localStorage.getItem('role');
      const jwtToken = localStorage.getItem('jwt');
      
      console.log('Shop ID from localStorage:', shopId);
      console.log('User role:', userRole);
      console.log('JWT token exists:', !!jwtToken);
      
      if (!shopId) return;
      
      console.log('Calling getEnhancedDeliveryPreference with shopId:', shopId);
      const response = await getEnhancedDeliveryPreference(shopId);
      console.log('API Response:', response);
      const data = response.data;
      
      // Business settings - using enhanced data structure
      setBusinessDeliveryPlatform(data.business_delivery_platform || 'BOTH');
      setEffectiveDeliveryPlatform(data.effective_delivery_platform || 'BOTH');
      setEffectiveWhatsappSendAs(data.effective_whatsapp_send_as || 'pdf');
      setShouldSendViaWhatsapp(data.should_send_via_whatsapp || true);
      setShouldPrint(data.should_print || true);
    } catch (err) {
      console.error('Delivery preferences error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
        shopId: localStorage.getItem('shopId'),
        userRole: localStorage.getItem('role'),
        hasToken: !!localStorage.getItem('jwt')
      });
      if (err.response?.status === 403) {
        setDeliveryError('Permission denied. Please contact admin to configure delivery platform preferences.');
      } else {
        setDeliveryError('Failed to fetch delivery preferences');
      }
    } finally {
      setDeliveryLoading(false);
    }
  };

  const updateBusinessDelivery = async () => {
    try {
      setDeliveryLoading(true);
      const shopId = localStorage.getItem('shopId');
      if (!shopId) return;
      
      await updateBusinessDeliveryPreference(shopId, businessDeliveryPlatform);
      setDeliverySuccess('Business delivery preferences updated successfully!');
      setDeliveryError('');
      
      // Refresh the data to get updated settings
      await fetchDeliveryPreferences();
    } catch (err) {
      console.error('Update delivery preferences error:', err);
      if (err.response?.status === 403) {
        setDeliveryError('Permission denied. Please contact admin to update delivery platform preferences.');
      } else {
        setDeliveryError('Failed to update business delivery preferences');
      }
      setDeliverySuccess('');
    } finally {
      setDeliveryLoading(false);
    }
  };

  const handleInvoiceChange = (e) => {
    setInvoice({ ...invoice, [e.target.name]: e.target.value });
  };

  const handleTabChange = (e, newValue) => {
    setTab(newValue);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleBusinessChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'logo' || name === 'logos') {
      if (files && files.length > 0) {
        setBusiness((prev) => ({ ...prev, logos: [...prev.logos, ...Array.from(files)] }));
        setLogoIndex(business.logos.length); // show the last added
      }
    } else {
      setBusiness({ ...business, [name]: value });
    }
  };

  const handleRemoveLogo = (idx) => {
    setBusiness((prev) => {
      const newLogos = prev.logos.filter((_, i) => i !== idx);
      return { ...prev, logos: newLogos };
    });
    setLogoIndex((prevIdx) => Math.max(0, Math.min(prevIdx, business.logos.length - 2)));
  };

  const handleSave = () => {
    // Placeholder for save action
    alert('Business settings saved (not really, just a demo)!');
  };

  // Section content renderers
  const renderSection = () => {
    switch (SECTIONS[tab].key) {
      case 'business':
      default:
        return (
          <Paper elevation={0} sx={{ 
            p: { xs: 2, sm: 3, md: 4 }, 
            width: '100%', 
            maxWidth: { xs: '100%', sm: 600 },
            border: '1px solid',
            borderColor: 'divider'
          }}>
            <Typography variant="h6" fontWeight={600} gutterBottom color="primary.main">
              Business Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Business Name</Typography>
                <Typography variant="body1" fontWeight={600}>{business.name}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Category</Typography>
                <Typography variant="body1">{business.category}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Contact Name</Typography>
                <Typography variant="body1">{business.contactName}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Contact Phone</Typography>
                <Typography variant="body1">{Array.isArray(business.contactPhone) ? business.contactPhone.join(', ') : business.contactPhone}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Contact Email</Typography>
                <Typography variant="body1">{business.contactEmail}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Address</Typography>
                <Typography variant="body1">{business.address}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">City</Typography>
                <Typography variant="body1">{business.city}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">State</Typography>
                <Typography variant="body1">{business.state}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Pin Code</Typography>
                <Typography variant="body1">{business.pinCode}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Complete Address</Typography>
                <Typography variant="body1">{business.completeAddress}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">GST Number</Typography>
                <Typography variant="body1">{business.gstNumber}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">GST Type</Typography>
                <Typography variant="body1">{business.gstType}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Country</Typography>
                <Typography variant="body1">{business.country}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Google Review ID</Typography>
                <Typography variant="body1">{business.googleReviewId}</Typography>
              </Box>
            </Stack>
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 3 }}>
              Contact admin for updates
            </Typography>
          </Paper>
        );
      case 'invoice':
        return (
          <Paper elevation={0} sx={{ 
            p: { xs: 2, sm: 3, md: 4 }, 
            width: '100%', 
            maxWidth: { xs: '100%', sm: 600 },
            border: '1px solid',
            borderColor: 'divider'
          }}>
            <Typography variant="h6" fontWeight={600} gutterBottom color="text.primary">
              Invoice Preferences
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="body1" color="text.secondary" align="center">
              Coming Soon.
            </Typography>
          </Paper>
        );
      case 'delivery':
        return (
          <Paper elevation={0} sx={{ 
            p: { xs: 2, sm: 3, md: 4 }, 
            width: '100%', 
            maxWidth: { xs: '100%', sm: 600 },
            border: '1px solid',
            borderColor: 'divider'
          }}>
            <Typography variant="h6" fontWeight={600} gutterBottom color="primary.main">
              Delivery Platform Settings
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            {deliveryError && <Alert severity="error" sx={{ mb: 2 }}>{deliveryError}</Alert>}
            {deliverySuccess && <Alert severity="success" sx={{ mb: 2 }}>{deliverySuccess}</Alert>}
            
            <Stack spacing={3}>
              {/* Delivery Platform Selection */}
              <Box>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Choose Your Delivery Platform
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Select how you want to deliver invoices to your customers
                </Typography>
                <RadioGroup
                  value={businessDeliveryPlatform}
                  onChange={(e) => setBusinessDeliveryPlatform(e.target.value)}
                >
                  <FormControlLabel 
                    value="WHATSAPP" 
                    control={<Radio />} 
                    label={
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="body2">WhatsApp Only</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Send via WhatsApp
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel 
                    value="PRINT" 
                    control={<Radio />} 
                    label={
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="body2">Print Only</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Print invoices
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel 
                    value="BOTH" 
                    control={<Radio />} 
                    label={
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="body2">Both WhatsApp and Print</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Recommended
                        </Typography>
                      </Box>
                    }
                  />
                </RadioGroup>
              </Box>

              {/* Effective Settings Display */}
              <Box>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  What Will Actually Happen
                </Typography>
                
                <Card variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: '#f1f8e9' }}>
                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Delivery Method
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body1" fontWeight={600}>
                          {effectiveDeliveryPlatform}
                        </Typography>
                        <CheckCircleIcon sx={{ color: 'success.main' }} />
                      </Box>
                    </Box>
                    
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        WhatsApp Send Mode
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {effectiveWhatsappSendAs === 'pdf' ? 'Send as PDF' : 'Send as Link'}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Actions
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        {shouldSendViaWhatsapp && (
                          <Chip 
                            label="WhatsApp" 
                            size="small" 
                            color="success" 
                            variant="outlined"
                          />
                        )}
                        {shouldPrint && (
                          <Chip 
                            label="Print" 
                            size="small" 
                            color="primary" 
                            variant="outlined"
                          />
                        )}
                      </Stack>
                    </Box>
                  </Stack>
                </Card>
              </Box>

              {/* Save Button */}
              <Button 
                variant="contained" 
                color="primary" 
                onClick={updateBusinessDelivery}
                disabled={deliveryLoading}
                sx={{ mt: 2 }}
                fullWidth={isMobile}
              >
                {deliveryLoading ? 'Updating...' : 'Update Delivery Platform'}
              </Button>
            </Stack>
          </Paper>
        );
    }
  };

  // Mobile navigation drawer
  const drawer = (
    <Box sx={{ width: 250 }}>
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h6" fontWeight={700} color="primary">
          Settings
        </Typography>
      </Box>
      <Tabs
        orientation="vertical"
        value={tab}
        onChange={handleTabChange}
        aria-label="Settings Sections"
        sx={{ height: '100%' }}
      >
        {SECTIONS.map((section, idx) => (
          <Tab
            key={section.key}
            label={<Typography fontWeight={tab === idx ? 700 : 500}>{section.label}</Typography>}
            sx={{ alignItems: 'flex-start', px: 3, py: 2, textAlign: 'left' }}
          />
        ))}
      </Tabs>
    </Box>
  );

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', pb: { xs: 2, sm: 0 } }}>
      {/* Mobile App Bar */}
      {isMobile && (
        <AppBar position="sticky" color="default" elevation={1}>
          <Toolbar>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={() => setMobileOpen(!mobileOpen)}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" fontWeight={700}>
              Business Settings
            </Typography>
          </Toolbar>
        </AppBar>
      )}

      <Box sx={{ 
        p: { xs: 1, sm: 2, md: 3 }, 
        maxWidth: 1200, 
        mx: 'auto'
      }}>
        {/* Desktop Header */}
        {!isMobile && (
          <Typography 
            variant="h4" 
            fontWeight={700} 
            gutterBottom 
            align="center" 
            sx={{ mb: 4 }}
          >
            Business Settings
          </Typography>
        )}

        {/* Desktop Layout */}
        {!isMobile && (
          <Paper elevation={0} sx={{ display: 'flex', minHeight: 500, maxWidth: 1000, mx: 'auto', border: '1px solid', borderColor: 'divider' }}>
            {/* Vertical Tabs Navigation */}
            <Box sx={{ borderRight: 1, borderColor: 'divider', minWidth: 220, bgcolor: 'background.paper', py: 6, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <Tabs
                orientation="vertical"
                value={tab}
                onChange={handleTabChange}
                aria-label="Settings Sections"
                variant="scrollable"
                sx={{ height: '100%' }}
              >
                {SECTIONS.map((section, idx) => (
                  <Tab
                    key={section.key}
                    label={<Typography fontWeight={tab === idx ? 700 : 500}>{section.label}</Typography>}
                    sx={{ alignItems: 'flex-start', px: 3, py: 2, textAlign: 'left' }}
                  />
                ))}
              </Tabs>
            </Box>
            {/* Section Content */}
            <Box sx={{ 
              flex: 1, 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center', 
              py: 6, 
              px: { xs: 2, sm: 3, md: 6 }, 
              minWidth: 0 
            }}>
              <Box sx={{ width: '100%', maxWidth: 600 }}>
                {renderSection()}
                {tab !== 2 && (
                  <Box sx={{ mt: 3, textAlign: 'center' }}>
                    <Button variant="contained" color="primary" onClick={handleSave} >
                      Save Changes
                    </Button>
                  </Box>
                )}
              </Box>
            </Box>
          </Paper>
        )}

        {/* Mobile Layout */}
        {isMobile && (
          <>
            {/* Mobile Drawer */}
            <Drawer
              variant="temporary"
              open={mobileOpen}
              onClose={() => setMobileOpen(false)}
              ModalProps={{
                keepMounted: true, // Better open performance on mobile.
              }}
              sx={{
                display: { xs: 'block' },
                '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 250 },
              }}
            >
              {drawer}
            </Drawer>

            {/* Mobile Content */}
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'flex-start',
              pt: 2
            }}>
              <Box sx={{ width: '100%', maxWidth: 600 }}>
                {renderSection()}
                {tab !== 2 && (
                  <Box sx={{ mt: 3, textAlign: 'center' }}>
                    <Button variant="contained" color="primary" onClick={handleSave}>
                      Save Changes
                    </Button>
                  </Box>
                )}
              </Box>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
} 